import request from '@/request/http/index';
import { NB_PHONE_REG, NB_ACCOUNT_REG } from '@/utils/consts';
import appInfo from '@/utils/appInfo';
import myWindow from '@/utils/myWindow';
import {
  getDeviceId,
  getDeviceVersion,
  goSupport,
  isUserInactiveForDays,
} from '@/utils/util';
import { store } from '@/store';
import { requestPost, requestPostPromise } from '@/request/http/http';
import { NBOcType } from '@/utils/emun';
import customStore from '@/request/http/store';
import { useCustomRouter } from '@/router/custRouter';
import hallLogic from './hallLogic';
import i18n from '@/locales';
import { showNbConfirmDialog } from '@/utils/dialog';
import cacheManager from '@/utils/cacheManager';
import {
  User_registration,
  sendGoalsGoogleTag,
  sendGoogleTag,
  sendClickGoogleTag,
  User_Churn_7d,
  User_Churn_1d,
  User_Churn_2d,
  User_Churn_Position,
} from '@/scripts/tagEvent';
import router, { clearScrollMap } from '@/router';

let googleAuthCallback: ((error: any) => void) | null = null;
let fbAuthCallback: ((error: any) => void) | null = null;

const loginLogic = {
  registerConfig: () => {
    return requestPostPromise(request.userApi.GetRegisterConfig, {
      siteType: 2,
    });
  },
  forgetPasswordConfig: () => {
    return requestPost(
      request.userApi.GetForgetPasswordConfig,
      null,
      (response) => {
        store.dispatch('user/actionsForgetPasswordConfigSet', response);
      }
    );
  },
  resetPassword: (params: object, callback: (arg0?: string) => void) => {
    requestPost(
      request.userApi.ResetPassword,
      params,
      (_, message) => {
        callback(message);
      },
      null,
      null,
      true
    );
  },
  loginAccountRequest: async (
    username: string,
    password: string,
    checked: boolean,
    fundCheck: () => void,
    funds?: string,
    fundsKey?: string,
    captcha?: string,
    captchaType?: string,
    codeError?: () => void
  ) => {
    const params = {
      signInType: NB_ACCOUNT_REG,
      userName: username,
      password,
      terminal: appInfo.terminalType,
      deviceUniqueId: await getDeviceId(),
      loginDeviceSystem: getDeviceVersion(),
      funds,
      fundsKey,
      captcha: captcha,
      captchaType: captcha ? captchaType : undefined,
      mobileModel: appInfo.mobileModel ?? undefined,
    };

    requestPost(
      request.userApi.Login,
      params,
      (response) => {
        if (checked) {
          cacheManager.increaseCache('user_login_account', {
            username,
            password,
          });
        } else {
          cacheManager.deleteCache('user_login_account');
        }

        handleLoginResult(response, '/login', !!funds && !!fundsKey);
        gtagLogin(response.userName);
        switch (true) {
          case isUserInactiveForDays(7):
            sendClickGoogleTag(
              User_Churn_7d,
              User_Churn_Position.UserChurnClick('User Loss For Seven Days')
            );
            return;

          case isUserInactiveForDays(2):
            sendClickGoogleTag(
              User_Churn_2d,
              User_Churn_Position.UserChurnClick('User Loss For Two Days')
            );
            return;

          case isUserInactiveForDays(1):
            sendClickGoogleTag(
              User_Churn_1d,
              User_Churn_Position.UserChurnClick('User Loss For One Days')
            );
            return;
        }
      },
      (error) => {
        error && handleLoginError(error, fundCheck, codeError);
      },
      null,
      true
    );
  },
  loginPhoneRequest: async (
    countryCode: string,
    mobile: string,
    password: string,
    checked: boolean,
    fundCheck: () => void,
    funds?: string,
    fundsKey?: string,
    captcha?: string,
    captchaType?: string,
    codeError?: () => void
  ) => {
    const params = {
      signInType: NB_PHONE_REG,
      countryCode,
      mobile,
      password,
      terminal: appInfo.terminalType,
      deviceUniqueId: await getDeviceId(),
      loginDeviceSystem: getDeviceVersion(),
      funds,
      fundsKey,
      captcha: captcha,
      captchaType: captcha ? captchaType : undefined,
      mobileModel: appInfo.mobileModel ?? undefined,
    };

    requestPost(
      request.userApi.Login,
      params,
      (response) => {
        if (checked) {
          cacheManager.increaseCache('user_login_phone', {
            mobile,
            countryCode,
            password,
          });
        } else {
          cacheManager.deleteCache('user_login_phone');
        }

        handleLoginResult(response, '/login', !!funds && !!fundsKey);
        gtagLogin(response.userName);
        switch (true) {
          case isUserInactiveForDays(7):
            sendClickGoogleTag(
              User_Churn_7d,
              User_Churn_Position.UserChurnClick('User Loss For Seven Days')
            );
            return;

          case isUserInactiveForDays(2):
            sendClickGoogleTag(
              User_Churn_2d,
              User_Churn_Position.UserChurnClick('User Loss For Two Days')
            );
            return;

          case isUserInactiveForDays(1):
            sendClickGoogleTag(
              User_Churn_1d,
              User_Churn_Position.UserChurnClick('User Loss For One Days')
            );
            return;
        }
      },
      (error) => {
        error && handleLoginError(error, fundCheck, codeError);
      },
      null,
      true
    );
  },
  signUpWithAccount: async (
    userName: string,
    password: string,
    repeatPassword: string,
    countryCode: string,
    mobile: string,
    smsCode: string,
    invitationCode: string,
    realName: string,
    oAuthType?: string,
    oAuthEmail?: string,
    oAuthId?: string,
    captcha?: string,
    captchaType?: string,
    codeError?: () => void,
    agentError?: () => void
  ) => {
    const params = {
      regType: NB_ACCOUNT_REG,
      userName: userName && userName.length > 0 ? userName : undefined,
      password: password && password.length > 0 ? password : undefined,
      repeatPassword:
        repeatPassword && repeatPassword.length > 0
          ? repeatPassword
          : undefined,
      countryCode:
        mobile && mobile.length > 0 && countryCode && countryCode.length > 0
          ? countryCode
          : undefined,
      mobile: mobile && mobile.length > 0 ? mobile : undefined,
      smsCode:
        mobile && mobile.length > 0 && smsCode && smsCode.length > 0
          ? smsCode
          : undefined,
      invitationCode:
        invitationCode && invitationCode.length > 0
          ? invitationCode
          : undefined,
      realName: realName && realName.length > 0 ? realName : undefined,
      currency: store.getters.currencyId,
      terminal: appInfo.terminalType,
      deviceUniqueId: await getDeviceId(),
      oAuth: oAuthType ? 1 : undefined,
      oAuthEmail,
      oAuthType,
      oAuthId,
      captcha,
      captchaType: captcha ? captchaType : undefined,
      appsflyerid: appInfo.appsFlyerId ?? undefined,
      appIds: appInfo.appIds ?? undefined,
      fbclid: appInfo.fbclid ?? undefined,
      // TOOD - API 文件未標明，而且 header 也有帶，待之後跟後端確認
      channelCode: appInfo.channelCode ?? undefined,
      mobileModel: appInfo.mobileModel ?? undefined,
    };

    requestPost(
      request.userApi.SignUp,
      params,
      (response) => {
        handleLoginResult(response, '/register');
        gtagRegister(oAuthType);
      },
      (error) => {
        error && handleLoginError(error, undefined, codeError, agentError);
      },
      null,
      true
    );
  },
  signUpWithPhone: async (
    countryCode: string,
    mobile: string,
    smsCode: string,
    password: string,
    repeatPassword: string,
    invitationCode: string,
    realName: string,
    oAuthType?: string,
    oAuthEmail?: string,
    oAuthId?: string,
    captcha?: string,
    captchaType?: string,
    codeError?: () => void,
    agentError?: () => void
  ) => {
    const params = {
      regType: NB_PHONE_REG,
      userName:
        mobile && mobile.length > 0 && countryCode && countryCode.length > 0
          ? countryCode + mobile
          : undefined,
      countryCode:
        mobile && mobile.length > 0 && countryCode && countryCode.length > 0
          ? countryCode
          : undefined,
      mobile: mobile && mobile.length > 0 ? mobile : undefined,
      smsCode:
        mobile && mobile.length > 0 && smsCode && smsCode.length > 0
          ? smsCode
          : undefined,
      password: password && password.length > 0 ? password : undefined,
      repeatPassword:
        repeatPassword && repeatPassword.length > 0
          ? repeatPassword
          : undefined,
      invitationCode:
        invitationCode && invitationCode.length > 0
          ? invitationCode
          : undefined,
      realName: realName && realName.length > 0 ? realName : undefined,
      currency: store.getters.currencyId,
      terminal: appInfo.terminalType,
      deviceUniqueId: await getDeviceId(),
      oAuth: oAuthType ? 1 : undefined,
      oAuthEmail,
      oAuthType,
      oAuthId,
      captcha: captcha,
      captchaType: captcha ? captchaType : undefined,
      appsflyerid: appInfo.appsFlyerId ?? undefined,
      appIds: appInfo.appIds ?? undefined,
      fbclid: appInfo.fbclid ?? undefined,
      // TOOD - API 文件未標明，而且 header 也有帶，待之後跟後端確認
      channelCode: appInfo.channelCode ?? undefined,
      mobileModel: appInfo.mobileModel ?? undefined,
    };

    requestPost(
      request.userApi.SignUp,
      params,
      (response) => {
        handleLoginResult(response, '/register');
        gtagRegister(oAuthType);
      },
      (error) => {
        error && handleLoginError(error, undefined, codeError, agentError);
      },
      null,
      true
    );
  },
  signInGuest: () => {
    requestPost(
      request.userApi.NBGuestSignUp,
      null,
      (response) => {
        const guest = {
          // 保存时间、token、identity
          startTime: Date.now(),
          token: customStore.get('token'),
          identity: customStore.get('identity'),
        };
        cacheManager.increaseCache(cacheManager.CacheKey.DEMO_ACCOUNT, guest);
        handleLoginResult(response, '/guest');
      },
      null,
      null,
      true
    );
  },
  signInWithGoogle: (callback: (error: any) => void) => {
    googleAuthCallback = callback;
    myWindow.googleSignIn && myWindow.googleSignIn();
  },
  signInWithFacebook: (callback: (error: any) => void) => {
    fbAuthCallback = callback;
    myWindow.facebookSignIn && myWindow.facebookSignIn();
  },
  oauthLoginAccess: async (
    type: string,
    token: string,
    funds?: string,
    fundsKey?: string
  ) => {
    const oauth = store.state.oauthConfig.find((v) => v.ocType === type);
    const params = {
      deviceId: await getDeviceId(),
      terminal: appInfo.terminalType,
      type,
      id: oauth?.ocId,
      oauthId: oauth?.ocOauthId,
      accessValue: {
        code: token,
      },
      funds,
      fundsKey,
    };

    requestPost(
      request.userApi.OAuthLoginAccess,
      params,
      (response) => {
        handleLoginResult(response, '/thirdLogin', !!funds && !!fundsKey);
        gtagLogin(response.userName);
      }, //认证成功表示存在账号，直接登录了
      (error) => {
        if (error) {
          if (type === NBOcType.Google && googleAuthCallback != null) {
            googleAuthCallback(error);
          } else if (type === NBOcType.Facebook && fbAuthCallback != null) {
            fbAuthCallback(error);
          }
        }
      },
      null,
      true
    );
  },
  thirdSignOut: () => {
    if (myWindow.googleOnSignOut) myWindow.googleOnSignOut();
    if (myWindow.FBLogout) myWindow.FBLogout();
  },
  fundsCheck: (
    signInType: string,
    funds: string,
    otherArgs: any,
    callback: (funds: string, fundsKey: string) => void
  ) => {
    const params = {
      signInType,
      funds,
      ...otherArgs,
    };

    requestPost(
      request.userApi.FundsCheck,
      params,
      (response) => {
        response && callback(funds, response);
      },
      null,
      null,
      true
    );
  },
  captchaCheck: (type: string, callback: (shouldCheck: boolean) => void) => {
    requestPost(request.userApi.CaptchaCheck, { type }, (response) => {
      callback(response.status === '1');
    });
  },
};

const handleLoginResult = (
  response: any,
  action: string,
  changeDevice?: boolean
) => {
  if (response) {
    if (
      action != '/guest' &&
      'guest' === router.currentRoute.value.query.role
    ) {
      // 登录注册成功(非试玩注册)，并且是试玩模式进入的登录注册页。需要清除试玩信息
      cacheManager.deleteCache(cacheManager.CacheKey.DEMO_ACCOUNT);
    }

    store.dispatch('user/updateUserInfo', response).then(() => {
      // 登录状态下进入时必须的请求
      hallLogic.getAllLoginRequest();
      // 重新获取游戏导航
      hallLogic.getSiteConfig();
      const customRouter = useCustomRouter(null);
      if (
        action == '/login' ||
        action == '/register' ||
        action == '/guest' ||
        action == '/thirdLogin'
      ) {
        const redirect = router.currentRoute.value.query.redirect;
        if (redirect === 'back') {
          // 登录后返回路由
          customRouter.back();
          showFundsDialog(action, changeDevice);
        } else {
          // 登录后跳转路由
          clearScrollMap();
          const dest = typeof redirect === 'string' ? redirect : '/';
          customRouter.replace(dest).then(() => {
            showFundsDialog(action, changeDevice);
          });
        }
      }
    });
  }
};

const showFundsDialog = (action: string, changeDevice?: boolean) => {
  if (changeDevice && (action == '/login' || action == '/thirdLogin')) {
    // 涉及资金密码登录，询问是否变更设备
    showNbConfirmDialog({
      message: i18n.global.t('dialog.CHANGE_DEVICE'),
      confirm: i18n.global.t('dialog.SETTING_BTN'),
    }).then(() => {
      useCustomRouter(null).push({ path: '/my/safety', query: { type: 3 } });
    });
  }
};

const handleLoginError = (
  error: any,
  fundCheck?: () => void,
  codeError?: () => void,
  agentError?: () => void
) => {
  if (error.errorcode === 101058 || error.errorcode === 101106) {
    goSupport();
  } else if (error.errorcode === 300052) {
    // 非绑定装置，登入需要传资金密码
    fundCheck && fundCheck();
  } else if (error.errorcode === 101001) {
    codeError && codeError();
  } else if (error.errorcode === 105016) {
    // 推广码达到上限
    showNbConfirmDialog({
      message: i18n.global.t('loginConfig.agentCodeError'),
    }).then(agentError);
  }
};

const gtagRegister = (thirdType?: string) => {
  let method = 'official';
  if (thirdType) {
    if (thirdType == NBOcType.Google) {
      method = 'google';
    } else if (thirdType == NBOcType.Facebook) {
      method = 'facebook';
    }
  }

  sendGoalsGoogleTag(User_registration, {
    method: method,
    event_label: 'User Registration Successful',
  });
};

const gtagLogin = (userName: string) => {
  sendGoogleTag(
    'login',
    {
      user: userName,
    },
    'submit'
  );
};

export default loginLogic;
